/**
 * GL8610XL Payroll JE Transformer (iSolved)
 *
 * Handles iSolved _GL_Report_*.xlsx exports:
 *   - Tabular source sheet (Sheet1): Date, Unit, Account, Amount, Desc
 *   - Keyword metadata from FINAL/other sheets: Company ID, Comments, Entry Status
 *
 * Output: GL8610XL keyword-based journal entry format, one JE per pay date.
 */
import * as XLSX from 'xlsx';
import { parseISolvedGLReport, formatAccountingDate } from '../lib/isolved-parser.js';
import { partitionZeroLines } from '../lib/amounts';

export function transformPayroll(_data, sourceWb) {
  const { rows: allRows, meta } = parseISolvedGLReport(sourceWb);
  const wb = XLSX.utils.book_new();
  const sheetData = [];

  // Payroll exports carry a $0 line for every benefit/tax an employee didn't
  // hit that period (FUTA, SUI, dental…). MV doesn't need them.
  const { kept: rows, zeroCount } = partitionZeroLines(allRows);
  meta.droppedZeroLines = zeroCount;

  // Group by accounting date (typically one pay run per file)
  const byDate = {};
  rows.forEach((row) => {
    const date = row.accounting_date || meta.accountingDate || formatAccountingDate(new Date());
    if (!byDate[date]) byDate[date] = [];
    byDate[date].push(row);
  });

  for (const [acctDate, periodRows] of Object.entries(byDate)) {
    const companyId = meta.companyId || '81';
    // Only emit the project columns when the source actually carries them —
    // keyword-format sources (staged FINAL sheets) map unit → project type/id,
    // and dropping those columns would silently lose that coding.
    const hasProjects = periodRows.some((row) => row.project_type || row.project_id);

    sheetData.push(['Company ID', companyId]);
    sheetData.push(['Accounting Date', acctDate]);
    sheetData.push(['Entry Type', meta.entryType || 'JE']);
    if (meta.entryStatus) {
      sheetData.push(['Entry Status', meta.entryStatus]);
    }
    sheetData.push(['Remarks', meta.remarks || `Payroll - ${acctDate}`]);

    const layoutCols = ['Unit', 'Account', 'Amount'];
    if (hasProjects) layoutCols.push('Project Type', 'Project Id');
    layoutCols.push('Remarks');
    sheetData.push(['Layout', ...layoutCols]);

    periodRows.forEach((row) => {
      // A null amount (no usable value) is written as 0; the validator errors on
      // it, so the download stays blocked until the source is fixed.
      const detailRow = ['Detail', row.unit, row.account, row.amount ?? 0];
      if (hasProjects) {
        detailRow.push(String(row.project_type ?? '').trim(), String(row.project_id ?? '').trim());
      }
      detailRow.push(row.description);
      sheetData.push(detailRow);
    });

    sheetData.push([]);
  }

  const ws = XLSX.utils.aoa_to_sheet(sheetData);
  XLSX.utils.book_append_sheet(wb, ws, 'PayrollJE');

  const previewRows = rows.map((row) => ({
    unit: row.unit,
    account: row.account,
    amount: row.amount,
    description: row.description,
    accounting_date: row.accounting_date,
    project_type: row.project_type ?? '',
    project_id: row.project_id ?? '',
  }));

  return { workbook: wb, rows: previewRows, meta };
}
